import { useEffect, useState, useCallback, useRef } from 'react';
import { IconX, IconCamera, IconCameraOff, IconRefresh, IconFileUpload } from '@tabler/icons-react';
import { QrReader, type OnResultFunction } from 'react-qr-reader';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import type { Result } from '@zxing/library';

// Logger utility function
const log = (message: string, data?: unknown) => {
  const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
  console.log(`[QRScanner ${timestamp}] ${message}`, data || '');
};

interface QRScannerProps {
  onClose: () => void;
  onScan: (data: string) => void;
}

interface QRData {
  type: 'address' | 'transaction' | 'url' | 'unknown';
  value: string;
}

type QRValidator = {
  type: QRData['type'];
  validate: (data: string) => boolean;
  transform?: (data: string) => string;
};

// Scanner modes for fallback strategy
type ScannerMode = 'react-qr-reader' | 'legacy-mode' | 'html5-qrcode';

const QR_VALIDATORS: QRValidator[] = [
  {
    type: 'address',
    validate: (data: string) => data.startsWith('0x') && data.length === 42,
    transform: (data: string) => data.toLowerCase().trim()
  },
  {
    type: 'url',
    validate: (data: string) => {
      try {
        new URL(data);
        return true;
      } catch {
        return false;
      }
    }
  }
];

const HTML5_SCAN_CONFIG = {
  fps: 10,
  qrbox: { width: 350, height: 350 },
  rememberLastUsedCamera: true,
  aspectRatio: 1.0,
  formatsToSupport: [
    Html5QrcodeSupportedFormats.QR_CODE,
    Html5QrcodeSupportedFormats.DATA_MATRIX,
    Html5QrcodeSupportedFormats.AZTEC
  ],
};

export default function QRScanner({ onClose, onScan }: QRScannerProps) {
  const [permissionState, setPermissionState] = useState<'prompt' | 'granted' | 'denied'>('prompt');
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string>('');
  const [scannerMode, setScannerMode] = useState<ScannerMode>('react-qr-reader');
  const lastScanRef = useRef<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const qrContainerRef = useRef<HTMLDivElement>(null);

  // Check if running as PWA and detect iOS
  const isPWA = window.matchMedia('(display-mode: standalone)').matches;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isIOSProblem = isIOS && (isPWA || isSafari);

  // Log device information
  useEffect(() => {
    log('Device info', {
      userAgent: navigator.userAgent,
      isPWA,
      isIOS,
      isSafari,
      isIOSProblem,
      hasMediaDevices: !!navigator.mediaDevices,
    });
  }, [isPWA, isIOS, isSafari, isIOSProblem]);

  // Handle error timeout cleanup
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (error) {
      log('Error set', error);
      timer = setTimeout(() => setError(''), 2000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [error]);

  // Cleanup function for HTML5QrCode
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        log('Cleaning up HTML5QrCode instance');
        html5QrCodeRef.current.stop().catch(err => log('Error stopping HTML5QrCode', err));
        html5QrCodeRef.current = null;
      }
    };
  }, []);

  // Check initial permission state
  useEffect(() => {
    let mounted = true;

    async function checkPermission() {
      log('Checking camera permissions');
      try {
        // First check if mediaDevices API is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          log('MediaDevices API not supported');
          if (mounted) {
            setError('Camera access is not supported in this browser');
            setPermissionState('denied');
          }
          return;
        }

        // For iOS Safari PWA, we need to handle permissions differently
        if (isIOSProblem) {
          log('Handling iOS Safari/PWA permissions');
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: {
                facingMode: 'environment',
                width: { ideal: 640 },
                height: { ideal: 480 }
              }
            });
            
            log('Successfully got camera stream on iOS');
            // Stop the stream immediately - we just wanted to check permission
            for (const track of stream.getTracks()) {
              track.stop();
            }
            
            if (mounted) {
              setPermissionState('granted');
              setIsScanning(true);
              // For iOS, start directly with html5-qrcode
              setScannerMode('html5-qrcode');
              log('Set scanner mode to html5-qrcode for iOS');
            }
          } catch (err) {
            if (!mounted) return;
            
            log('iOS camera permission error', err);
            const error = err as { name?: string };
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
              setPermissionState('prompt');
              log('Camera permission denied, showing prompt');
            } else {
              setPermissionState('denied');
              setError('Camera access failed. Please try again.');
              log('Camera access failed with error', error.name);
            }
          }
          return;
        }

        // Try the Permissions API for non-iOS devices
        if (navigator.permissions?.query) {
          log('Using Permissions API to check camera access');
          const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
          log('Permission state from API', result.state);
          
          if (mounted) {
            setPermissionState(result.state as 'prompt' | 'granted' | 'denied');
            if (result.state === 'granted') {
              setIsScanning(true);
              log('Camera permission already granted');
            }
          }
          
          result.onchange = () => {
            if (mounted) {
              log('Permission state changed', result.state);
              setPermissionState(result.state as 'prompt' | 'granted' | 'denied');
              if (result.state === 'granted') {
                setIsScanning(true);
              }
            }
          };
        } else {
          // Fallback: Try to enumerate devices
          log('Permissions API not available, falling back to enumerateDevices');
          const devices = await navigator.mediaDevices.enumerateDevices();
          const cameras = devices.filter(device => device.kind === 'videoinput');
          log('Found camera devices', cameras.length);
          
          if (mounted) {
            if (cameras.length === 0) {
              setError('No camera found on this device');
              setPermissionState('denied');
              log('No cameras found on device');
            } else {
              setPermissionState('prompt');
              log('Cameras found, setting permission state to prompt');
            }
          }
        }
      } catch (err) {
        if (mounted) {
          console.error('Permission check failed:', err);
          log('Permission check failed with error', err);
          setPermissionState('prompt');
        }
      }
    }

    checkPermission();

    return () => {
      log('Permission check cleanup');
      mounted = false;
    };
  }, [isIOSProblem]);

  // Initialize HTML5QrCode scanner
  useEffect(() => {
    if (scannerMode !== 'html5-qrcode' || !qrContainerRef.current || permissionState !== 'granted') {
      return;
    }

    const startHtml5QrCode = async () => {
      log('Starting HTML5QrCode scanner');
      try {
        // Create HTML5 QR instance
        html5QrCodeRef.current = new Html5Qrcode("qr-reader", { 
          verbose: false 
        });
        log('HTML5QrCode instance created');

        await html5QrCodeRef.current.start(
          { facingMode: "environment" },
          {
            ...HTML5_SCAN_CONFIG,
            videoConstraints: {
              width: { ideal: isIOS ? 1280 : 1920 },
              height: { ideal: isIOS ? 720 : 1080 },
              facingMode: "environment",
              aspectRatio: 16/9
            }
          },
          (decodedText) => {
            // Handle successful scan
            log('HTML5QrCode scan success', `${decodedText.substring(0, 20)}...`);
            handleSuccessfulScan(decodedText);
          },
          (errorMessage) => {
            // Ignore intermediate errors
            if (!errorMessage.includes('No QR code found')) {
              log('HTML5QrCode scan error', errorMessage);
            }
          }
        );
        
        log('HTML5QrCode started successfully');
        setIsScanning(true);
        setError('');
      } catch (err) {
        log('HTML5QrCode error', err);
        console.error('HTML5QrCode error:', err);
        setError('Failed to start camera. Please try again.');
        
        // If html5-qrcode fails on iOS, we'll try the legacy mode
        if (isIOSProblem) {
          log('Switching to legacy-mode after HTML5QrCode failure on iOS');
          setScannerMode('legacy-mode');
        }
      }
    };

    startHtml5QrCode();

    return () => {
      if (html5QrCodeRef.current) {
        log('Cleaning up HTML5QrCode on effect unmount');
        html5QrCodeRef.current.stop().catch(console.error);
      }
    };
  }, [scannerMode, permissionState, isIOSProblem, isIOS]);

  const validateQRData = useCallback((data: string): QRData => {
    log('Validating QR data', `${data.substring(0, 20)}...`);
    try {
      // Try to parse as JSON first
      const jsonData = JSON.parse(data);
      log('Parsed QR data as JSON', jsonData);
      if (jsonData.address) {
        log('Found address in JSON data');
        return { type: 'address', value: jsonData.address };
      }
      if (jsonData.tx) {
        log('Found transaction in JSON data');
        return { type: 'transaction', value: jsonData.tx };
      }
    } catch {
      log('QR data is not valid JSON, trying validators');
      // Not JSON, try validators
      for (const validator of QR_VALIDATORS) {
        if (validator.validate(data)) {
          log(`Validated as ${validator.type}`);
          return {
            type: validator.type,
            value: validator.transform ? validator.transform(data) : data
          };
        }
      }
    }
    log('Unrecognized QR data format');
    return { type: 'unknown', value: data };
  }, []);

  const handleSuccessfulScan = useCallback((rawText: string) => {
    // Debounce scans
    const now = Date.now();
    if (now - lastScanRef.current < 1000) {
      log('Ignoring scan due to debounce');
      return;
    }
    lastScanRef.current = now;
    log('Processing QR scan', `${rawText.substring(0, 20)}...`);

    try {
      const qrData = validateQRData(rawText);
      log('QR data validated as', qrData);
      
      switch (qrData.type) {
        case 'address': {
          const cleanAddress = qrData.value.toLowerCase().trim();
          log('Processing address', cleanAddress);
          if (!/^0x[a-f0-9]{40}$/i.test(cleanAddress)) {
            log('Invalid Ethereum address format');
            throw new Error('Invalid Ethereum address format');
          }
          log('Valid address found, calling onScan');
          onScan(cleanAddress);
          onClose();
          break;
        }

        case 'transaction': {
          log('Transaction data found, calling onScan');
          onScan(qrData.value);
          onClose();
          break;
        }

        case 'url': {
          log('URL found, calling onScan');
          onScan(qrData.value);
          onClose();
          break;
        }

        default: {
          log('Unsupported QR code format');
          setError('Unsupported QR code format');
          break;
        }
      }
    } catch (err) {
      log('QR processing error', err);
      setError(err instanceof Error ? err.message : 'Invalid QR code format');
    }
  }, [onClose, onScan, validateQRData]);

  // Handler for react-qr-reader
  const handleQRScan: OnResultFunction = useCallback((result, error) => {
    if (error && !error.message?.includes('No QR code found')) {
      log('react-qr-reader error', error);
      
      // If we've had consistent errors, try switching to HTML5QrCode
      if (scannerMode === 'react-qr-reader') {
        log('Switching to HTML5QrCode due to errors');
        setScannerMode('html5-qrcode');
        return;
      }
      
      setError('Failed to scan QR code');
      return;
    }

    if (!result?.getText) {
      return;
    }

    try {
      log('react-qr-reader scan success');
      const rawText = result.getText();
      handleSuccessfulScan(rawText);
    } catch (err) {
      log('Error getting text from QR result', err);
      setError(err instanceof Error ? err.message : 'Invalid QR code format');
    }
  }, [scannerMode, handleSuccessfulScan]);

  // Handle legacy mode file upload
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    log('File selected for QR scanning', file.name);
    if (scannerMode === 'legacy-mode') {
      if (fileInputRef.current) {
        const fileUrl = URL.createObjectURL(file);
        log('Created object URL for file');
        try {
          const html5QrCode = new Html5Qrcode("qr-reader");
          log('Scanning file for QR code');
          const result = await html5QrCode.scanFile(file, true);
          log('File scan success', `${result.substring(0, 20)}...`);
          handleSuccessfulScan(result);
        } catch (err) {
          log('File scanning error', err);
          console.error('File scanning error:', err);
          setError('Failed to scan QR code from image');
        } finally {
          URL.revokeObjectURL(fileUrl);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      }
    }
  }, [scannerMode, handleSuccessfulScan]);

  const requestPermission = useCallback(async () => {
    log('Requesting camera permission');
    try {
      setError('');
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        log('MediaDevices API not supported');
        setError('Camera access is not supported in this browser');
        setPermissionState('denied');
        return;
      }

      log('Requesting camera access with constraints', {
        facingMode: 'environment',
        width: isIOS ? 640 : 1280,
        height: isIOS ? 480 : 720
      });
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: isIOS ? 640 : 1280 },
          height: { ideal: isIOS ? 480 : 720 }
        } 
      });
      
      log('Camera permission granted, stopping test stream');
      // Stop the stream immediately - we just wanted to get permission
      for (const track of stream.getTracks()) {
        track.stop();
      }
      
      log('Setting permission state to granted');
      setPermissionState('granted');
      setIsScanning(true);
      
      // For iOS devices, start with html5-qrcode directly
      if (isIOSProblem) {
        log('Setting scanner mode to html5-qrcode for iOS');
        setScannerMode('html5-qrcode');
      }
    } catch (err) {
      log('Permission request error', err);
      console.error('Permission error:', err);
      setPermissionState('denied');
      
      const error = err as { name?: string };
      if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        log('No camera found error');
        setError('No camera found on this device');
      } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        log('Camera permission denied');
        setError(isIOS ? 
          'Camera access was denied. Please check your iOS Settings app > Safari > Camera.' :
          'Camera access was denied. Please enable it in your browser settings.'
        );
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        log('Camera in use by another application');
        setError('Camera is in use by another application');
      } else {
        log('Generic camera access error');
        setError('Failed to access camera. Please try again.');
      }
    }
  }, [isIOS, isIOSProblem]);

  // Toggle scanner mode when having issues
  const toggleScannerMode = useCallback(() => {
    log('Toggling scanner mode from', scannerMode);
    if (scannerMode === 'react-qr-reader') {
      setScannerMode('html5-qrcode');
      log('Switched to html5-qrcode mode');
    } else if (scannerMode === 'html5-qrcode') {
      setScannerMode('legacy-mode');
      log('Switched to legacy-mode');
    } else {
      setScannerMode('react-qr-reader');
      log('Switched to react-qr-reader mode');
    }
    
    // Stop existing html5-qrcode instance if it exists
    if (html5QrCodeRef.current) {
      log('Stopping existing HTML5QrCode instance');
      html5QrCodeRef.current.stop().catch(err => log('Error stopping HTML5QrCode', err));
      html5QrCodeRef.current = null;
    }
    
    setError('');
  }, [scannerMode]);

  // Trigger file input click
  const triggerFileUpload = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Update video constraints for wider viewport
  const videoConstraints = {
    width: { ideal: isIOS ? 1280 : 1920 }, // Increased resolution
    height: { ideal: isIOS ? 720 : 1080 },
    facingMode: "environment",
    aspectRatio: 16/9 // Wider aspect ratio
  };

  return (
    <dialog 
      className="fixed inset-0 bg-black z-50 flex flex-col m-0 w-full h-full"
      open
      aria-modal="true"
      aria-label="QR Code Scanner"
    >
      <div className="safe-top flex justify-between items-center p-4">
        <div className="flex gap-2">
          {permissionState === 'granted' && (
            <button
              type="button"
              onClick={toggleScannerMode}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-surface/80"
              aria-label="Change scanning mode"
            >
              <IconRefresh size={20} />
            </button>
          )}
          {scannerMode === 'legacy-mode' && (
            <button
              type="button"
              onClick={triggerFileUpload}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-surface/80"
              aria-label="Upload QR code image"
            >
              <IconFileUpload size={20} />
            </button>
          )}
        </div>
        <h2 className="text-white text-lg font-medium">
          Scan QR Code
          {scannerMode !== 'react-qr-reader' && (
            <span className="text-xs ml-2 opacity-75">
              ({scannerMode === 'html5-qrcode' ? 'Advanced Mode' : 'Upload Mode'})
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-surface/80"
          aria-label="Close QR Scanner"
        >
          <IconX size={20} />
        </button>
      </div>

      <div className="flex-1 relative">
        {error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-4 z-10">
            <IconCameraOff size={48} className="text-red-500" aria-hidden="true" />
            <p className="text-center text-white" role="alert">{error}</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {permissionState === 'denied' ? (
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-full bg-surface text-sm"
                  aria-label="Close Camera"
                >
                  Close Camera
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={requestPermission}
                    className="px-4 py-2 rounded-full bg-surface text-sm"
                    aria-label="Try scanning again"
                  >
                    Try Again
                  </button>
                  <button
                    type="button"
                    onClick={toggleScannerMode}
                    className="px-4 py-2 rounded-full bg-surface text-sm"
                    aria-label="Try different scan mode"
                  >
                    Try Different Mode
                  </button>
                </>
              )}
            </div>
          </div>
        ) : null}

        <div 
          id="qr-reader" 
          ref={qrContainerRef}
          className={`h-full w-full ${scannerMode === 'html5-qrcode' ? 'block' : 'hidden'}`}
        />

        {permissionState === 'granted' && scannerMode === 'react-qr-reader' && (
          <QrReader
            constraints={videoConstraints}
            onResult={handleQRScan}
            scanDelay={isIOS ? 500 : 100}
            className="h-full w-full"
            videoStyle={{ 
              objectFit: 'cover', 
              width: '100%', 
              height: '100%',
              aspectRatio: '16/9' // Wider aspect ratio
            }}
            containerStyle={{ 
              width: '100%', 
              height: '100%',
              aspectRatio: '16/9' // Wider aspect ratio
            }}
          />
        )}

        {permissionState === 'granted' && scannerMode === 'legacy-mode' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10">
            <IconFileUpload size={48} className="text-white" aria-hidden="true" />
            <p className="text-center text-white">
              Upload a QR code image to scan
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              aria-label="Upload QR code image"
            />
            <button
              type="button"
              onClick={triggerFileUpload}
              className="px-4 py-2 rounded-full bg-surface text-sm"
              aria-label="Upload QR code image"
            >
              Upload Image
            </button>
          </div>
        )}

        {!isScanning && !error && permissionState !== 'granted' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10">
            <IconCamera size={48} aria-hidden="true" />
            <p className="text-center text-white">
              Camera access is required to scan QR codes
            </p>
            <button
              type="button"
              onClick={requestPermission}
              className="px-4 py-2 rounded-full bg-surface text-sm"
              aria-label="Enable camera access"
            >
              Enable Camera
            </button>
          </div>
        )}
      </div>
    </dialog>
  );
} 